import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { fmtDate, fmtTime } from "@/lib/api";
import { Skeleton, Empty } from "@/components/brand";
import { useLoad } from "@/components/app";
import { KpiTile } from "@/pages/Owner";

export default function OwnerInsights() {
  const [days, setDays] = useState(30);
  const { data } = useLoad(`/owner/insights?days=${days}`);
  const k = data?.kpis;
  const maxGoal = Math.max(1, ...(data?.goals || []).map((g) => g.count));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="f-display text-4xl">Insights</h1>
        <div className="flex">{[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} className={`btn btn-sm ${days === d ? "btn-black" : "btn-outline"}`} data-testid={`insights-range-${d}`}>{d} days</button>)}</div>
      </div>
      {!data ? <Skeleton className="h-96" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <KpiTile testid="ins-visitors" label="Website visitors" value={k.visitors} />
            <KpiTile testid="ins-consults" label="Consult requests" value={k.consults} bar="bg-red7" sub={`${k.consultRate}% of visitors`} />
            <KpiTile testid="ins-clients" label="Became clients" value={k.clients} bar="bg-gold" sub={`${k.clientRate}% of consults`} />
            <KpiTile testid="ins-referrals" label="Refer views / sent" value={`${k.referViews} / ${k.referralsSent}`} bar="bg-red7" />
            <KpiTile testid="ins-logins" label="Portal logins" value={k.portalLogins} />
            <KpiTile testid="ins-pricing" label="Pricing views" value={k.pricingViews} bar="bg-gold" />
          </div>
          <div className="bg-white border border-tanline p-6" data-testid="visitors-chart">
            <p className="eyebrow text-muted7">Visitors per day</p>
            <div className="h-60 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.perDay} margin={{ left: -20, right: 0 }}>
                  <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d, { month: "numeric", day: "numeric" })} tick={{ fontSize: 10, fill: "#5E584D" }} axisLine={{ stroke: "#E6DCC8" }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#5E584D" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#FAF6EE" }} contentStyle={{ borderRadius: 0, border: "1px solid #E6DCC8", fontSize: 12 }} labelFormatter={(d) => fmtDate(d)} />
                  <Bar dataKey="visitors">{data.perDay.map((d, i) => <Cell key={i} fill={d.spike ? "#D9A520" : "#111111"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="bg-white border border-tanline p-6" data-testid="interest-card">
              <p className="eyebrow text-muted7">What people are interested in</p>
              <ul className="mt-4 space-y-4">{data.goals.map((g) => (
                <li key={g.goal}><div className="flex justify-between text-sm"><span className="font-medium">{g.goal}</span><span>{g.count}</span></div>
                  <div className="h-2 bg-tan mt-1.5"><div className="h-full bg-gold" style={{ width: `${(g.count / maxGoal) * 100}%` }} /></div></li>
              ))}</ul>
            </div>
            <div className="bg-white border border-tanline p-6" data-testid="sources-card">
              <p className="eyebrow text-muted7">Sources</p>
              <table className="w-full text-sm mt-3"><thead><tr className="text-left f-mono text-[10px] uppercase text-muted7"><th className="py-2">Source</th><th>Visits</th><th>Consults</th></tr></thead>
                <tbody>{data.sources.map((s) => <tr key={s.source} className="border-t border-tanline"><td className="py-2.5 font-medium">{s.source}</td><td>{s.visits}</td><td>{s.consults}</td></tr>)}</tbody></table>
            </div>
            <div className="bg-white border border-tanline p-6" data-testid="pages-card">
              <p className="eyebrow text-muted7">Most viewed pages</p>
              <ul className="mt-3 divide-y divide-tanline">{data.pages.map((p) => <li key={p.path} className="py-2.5 flex justify-between text-sm"><span className="f-mono">{p.path}</span><span>{p.views}</span></li>)}</ul>
            </div>
            <div className="bg-white border border-tanline p-6" data-testid="portal-activity">
              <p className="eyebrow text-muted7">Client portal activity</p>
              {!data.portalActivity.length ? <p className="text-sm text-muted7 mt-3">No portal activity yet.</p> : <ul className="mt-3 divide-y divide-tanline">{data.portalActivity.map((a, i) => <li key={i} className="py-2.5 flex justify-between text-sm gap-3"><span>{a.text}</span><span className="f-mono text-[10px] text-muted7 shrink-0">{fmtTime(a.ts)}</span></li>)}</ul>}
            </div>
          </div>
          <div className="bg-white border border-tanline p-6" data-testid="hot-visitors">
            <p className="eyebrow text-red7">Hot visitors · viewed pricing 3+ times without booking</p>
            {!data.hotVisitors.length ? <Empty title="No hot visitors in this range." /> : (
              <div className="overflow-x-auto"><table className="w-full text-sm mt-3 min-w-[520px]"><thead><tr className="text-left f-mono text-[10px] uppercase text-muted7"><th className="py-2">Visitor</th><th>Source</th><th>Pricing views</th><th>Pages</th><th>Last seen</th></tr></thead>
                <tbody>{data.hotVisitors.map((h) => <tr key={h.visitor} className="border-t border-tanline"><td className="py-2.5 font-medium">{h.visitor}</td><td>{h.source}</td><td>{h.pricingViews}</td><td className="f-mono text-xs">{h.pages.join(", ")}</td><td className="f-mono text-[10px]">{fmtTime(h.last)}</td></tr>)}</tbody></table></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
